import { create } from 'zustand';

import { validateConfig, getAutoPerformanceConfig, VB_PRESETS } from './virtualBackground.presets';

import { detectDeviceCapabilities, supportsWebGL2 } from '@src/utils';

import { TBackgroundEffect, IUploadedImage, IVirtualBackgroundConfig } from '@src/types';

export const DEFAULT_VB_CONFIG: IVirtualBackgroundConfig = {
  // Performance - Optimized for lower CPU/memory usage
  inferenceScale: 0.5,
  segmentationFPS: 15,
  delegate: 'GPU',

  // Quality
  smoothing: 0.4, // Combines mask + temporal smoothing
  edgeBlur: 2, // Cleaner edges, min 3 enforced by anti-flicker

  // AI Engine
  segmentationBackend: 'mediapipe',

  // Auto-Performance Mode - enabled by default
  autoPerformanceMode: true,

  // Renderer Selection - Smart auto-detection
  // WebGL provides 70% CPU reduction on capable devices
  // Falls back to Canvas2D on mobile or if WebGL2 unavailable
  rendererType: (() => {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const hasWebGL2 = supportsWebGL2();

    // Use WebGL on desktop with WebGL2 support, Canvas2D otherwise
    return !isMobile && hasWebGL2 ? 'webgl' : 'canvas2d';
  })(),

  // Preset
  preset: 'quality',

  ...VB_PRESETS.quality,
};

/**
 * Global Virtual Background State
 * Single source of truth for virtual background effects
 *
 * Pattern: Single state (activeEffect)
 * - When user selects effect → immediately becomes active
 * - No preview/apply separation (maintains single worker instance)
 * - All components consume the same processed stream
 */
/**
 * Global Virtual Background State
 * Single source of truth for virtual background effects
 *
 * Refactored for Preview Feature:
 * - appliedEffect: The effect currently burned into the stream (Live)
 * - selectedEffect: The effect currently selected in UI (Preview)
 */
interface IVirtualBackgroundState {
  // Current applied effect (Visible to others)
  appliedEffect: TBackgroundEffect;

  // Currently selected effect (Preview only)
  selectedEffect: TBackgroundEffect;

  // Advanced configuration
  config: IVirtualBackgroundConfig;

  // Device capabilities (detected once)
  deviceCapabilities: {
    isMobile: boolean;
    hasGPU: boolean;
    cpuCores: number;
    memoryGB: number;
    performanceTier: 'low' | 'medium' | 'high';
  } | null;

  // UI state
  isBackgroundEffectsOpen: boolean;
  isAdvancedSettingsOpen: boolean;

  // Uploaded custom backgrounds
  uploadedBackgrounds: IUploadedImage[];

  // Actions
  setAppliedEffect: (effect: TBackgroundEffect) => void;
  setSelectedEffect: (effect: TBackgroundEffect) => void;

  // Commit selection to applied (Click "Apply")
  applySelectedEffect: () => void;
  // Discard selection and revert to applied (Click "Cancel" or Close)
  discardSelectedEffect: () => void;

  setConfig: (config: Partial<IVirtualBackgroundConfig>) => void;
  setAutoPerformanceMode: (enabled: boolean) => void;
  detectAndApplyPerformanceSettings: () => Promise<void>;
  setIsBackgroundEffectsOpen: (isOpen: boolean) => void;
  setIsAdvancedSettingsOpen: (isOpen: boolean) => void;
  appendUploadedBackground: (background: IUploadedImage) => void;
  removeUploadedBackground: (id: number) => void;
  reset: () => void;
}

const DEFAULT_EFFECT: TBackgroundEffect = {
  type: 'none',
  value: null,
};

export const useVirtualBackgroundStore = create<IVirtualBackgroundState>((set, get) => ({
  // Initial state
  appliedEffect: DEFAULT_EFFECT,
  selectedEffect: DEFAULT_EFFECT,
  config: DEFAULT_VB_CONFIG,
  deviceCapabilities: null,
  isBackgroundEffectsOpen: false,
  isAdvancedSettingsOpen: false,
  uploadedBackgrounds: [],

  // Set applied effect directly (e.g., initial load or simplified mode)
  setAppliedEffect: (effect) => {
    set({ appliedEffect: effect, selectedEffect: effect });
  },

  // Set selected effect (Preview updates, Applied unchanged)
  setSelectedEffect: (effect) => {
    set({ selectedEffect: effect });
  },

  // Apply: Copy Selected -> Applied
  applySelectedEffect: () => {
    const { selectedEffect } = get();
    set({ appliedEffect: selectedEffect });
  },

  // Discard: Copy Applied -> Selected
  discardSelectedEffect: () => {
    const { appliedEffect } = get();
    set({ selectedEffect: appliedEffect });
  },

  // Update configuration (partial updates allowed with validation)
  setConfig: (partialConfig) => {
    const validatedConfig = validateConfig(partialConfig);
    set((state) => ({
      config: { ...state.config, ...validatedConfig },
    }));
  },

  // Toggle auto-performance mode
  setAutoPerformanceMode: (enabled) => {
    set((state) => ({
      config: { ...state.config, autoPerformanceMode: enabled },
    }));

    // If enabling, immediately detect and apply settings
    if (enabled) {
      get().detectAndApplyPerformanceSettings();
    }
  },

  // Detect device capabilities and apply optimal settings
  detectAndApplyPerformanceSettings: async () => {
    const state = get();

    // Skip if auto-performance mode is disabled
    if (!state.config.autoPerformanceMode) return;

    // skip if already detected
    if (state.deviceCapabilities !== null) {
      return;
    }

    try {
      const capabilities = await detectDeviceCapabilities();
      let autoConfig = getAutoPerformanceConfig(capabilities);

      // Preserve user's segmentationBackend choice
      // If RVM is explicitly selected, don't override with MediaPipe-based settings
      const currentBackend = state.config.segmentationBackend;
      if (currentBackend === 'rvm') {
        // For RVM, use RVM-specific settings adjusted for device capability
        // RVM is slower than MediaPipe, so we use lower FPS regardless of benchmark
        autoConfig = {
          ...autoConfig,
          segmentationBackend: 'rvm',
          preset: 'rvm',
          segmentationFPS: Math.min(autoConfig.segmentationFPS || 15, 10), // RVM max is 10 FPS for single-threaded
          inferenceScale: Math.min(autoConfig.inferenceScale || 0.5, 0.2), // RVM ultra-low 0.2 for single-threaded test
          smoothing: 0.1, // Reduced smoothing
        };
      }

      console.log('[VB Store] Auto-performance detection:', {
        capabilities,
        appliedConfig: autoConfig,
      });

      set({
        deviceCapabilities: capabilities,
        config: {
          ...state.config,
          ...autoConfig,
          autoPerformanceMode: true, // Keep auto mode enabled
          // Always preserve the user's backend choice
          segmentationBackend: currentBackend,
        },
      });
    } catch (error) {
      console.error('[VB Store] Failed to detect device capabilities:', error);
    }
  },

  // Modal state
  setIsBackgroundEffectsOpen: (isOpen) => {
    // When opening, ensure selection matches currently applied
    if (isOpen) {
      const { appliedEffect } = get();
      set({ isBackgroundEffectsOpen: true, selectedEffect: appliedEffect });
    } else {
      // When closing directly (without apply), revert selection
      const { appliedEffect } = get();
      set({ isBackgroundEffectsOpen: false, selectedEffect: appliedEffect });
    }
  },

  setIsAdvancedSettingsOpen: (isOpen) => {
    set({ isAdvancedSettingsOpen: isOpen });
  },

  // Custom backgrounds
  appendUploadedBackground: (background) => {
    set((state) => ({
      uploadedBackgrounds: [background, ...state.uploadedBackgrounds],
    }));
  },

  removeUploadedBackground: (id) => {
    set((state) => ({
      uploadedBackgrounds: state.uploadedBackgrounds.filter((bg) => bg.id !== id),
    }));
  },

  // Reset to defaults
  reset: () => {
    set({
      appliedEffect: DEFAULT_EFFECT,
      selectedEffect: DEFAULT_EFFECT,
      config: DEFAULT_VB_CONFIG,
      deviceCapabilities: null,
      isBackgroundEffectsOpen: false,
      isAdvancedSettingsOpen: false,
      uploadedBackgrounds: [],
    });
  },
}));
